import React, { useContext, useEffect, useMemo, useState } from 'react';
import { CountryCode } from '@dayetopia/library/types';
import styled from 'styled-components';
import ArticleItem from '@components/vitals/ArticleItem';
import { NAV_MENU_BREAKPOINT, NAV_SEARCH_VITALS_INITIAL_COUNT, NAV_SEARCH_VITALS_SHOW_MORE_COUNT } from '@constants';
import { AlgoliaHit, Article, NavStatus } from '@contracts';
import { useCountry } from '@hooks/useCountry';
import { getArticleInfo } from '@prismicVitals';
import { NavContext } from './Context';

interface Props {
  hits: AlgoliaHit[];
  query: string;
  shouldLoadMore: boolean;
  setVitalsResultsCount: React.Dispatch<React.SetStateAction<number>>;
}

function NavSearchArticleHits({ hits, query, shouldLoadMore, setVitalsResultsCount }: Props) {
  const { country } = useCountry();
  const [articles, setArticles] = useState<Article[]>([]);
  const { navDispatch } = useContext(NavContext);

  useEffect(() => {
    getArticleInfo(setArticles, 1000, country === CountryCode.US);
  }, [country]);

  const filteredArticles = useMemo(() => {
    return articles.filter((article) => {
      if (query) {
        const isHitContainingArticle = hits.some((hit) => {
          const splittedHitUrl = hit.path.split('/');
          const articleIdFromUrl = splittedHitUrl[splittedHitUrl.length - 1] || splittedHitUrl[splittedHitUrl.length - 2];

          return articleIdFromUrl === article.uid;
        });

        return isHitContainingArticle;
      }

      return true;
    });
  }, [query, shouldLoadMore, hits, articles]);

  useEffect(() => {
    setVitalsResultsCount(filteredArticles.length);
  }, [filteredArticles]);

  const onHitClick = () => {
    navDispatch({ type: 'setStatus', data: NavStatus.Default });
    navDispatch({ type: 'removeNavbarSelectedCategories' });
  };

  return (
    <Wrapper>
      <GridWrapper>
        {filteredArticles.slice(0, shouldLoadMore ? NAV_SEARCH_VITALS_SHOW_MORE_COUNT : NAV_SEARCH_VITALS_INITIAL_COUNT).map((article) => (
          <HitWrapper key={article.uid}>
            <ArticleItem article={article} callback={onHitClick} noMobileDesign />
          </HitWrapper>
        ))}
      </GridWrapper>
    </Wrapper>
  );
}

export default NavSearchArticleHits;

const Wrapper = styled.div``;

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(250px, 1fr));
  gap: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 400px) {
    grid-template-columns: repeat(1, 1fr);
    margin-bottom: 0;
  }
`;

const HitWrapper = styled.div`
  margin-bottom: 20px;

  > div {
    margin-right: 0;
    width: 100%;
  }

  @media (max-width: ${NAV_MENU_BREAKPOINT}px) {
    margin-bottom: 5px;
    display: flex;
    justify-content: center;

    .article-container {
      margin-right: 0;
    }
  }

  a,
  img {
    border-radius: 10px;
  }
`;
